import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import UITemplateTypes, { listTemplateTypes } from './templates/types';
import { Blank, IntroSplash, Title, EULA, Branch, FinishProcedure, Content, TitleMenu6, TitleMenu12, FullPageText, Custom, QuizResults } from './templates';
import CustomSlideEditor from './customEditor/customSlideEditor';
import UITrackersEditor from '../trackersUI/UITrackersEditor';
import PropertyGroup from '../properties/PropertyGroup';
import DropdownProperty from '../properties/DropdownProperty';
import Panel from '../layout/Panel';
import store, { StoreActions } from '../../state/store';
import ResetTemplateAction from '../../actions/ui/ResetTemplateAction';
import Button from '../layout/Button';
import { PencilIcon, SyncUITemplateIcon, UiTemplateIcon, TrashIcon } from '../Iconset';
import Spacer from '../layout/Spacer';
import DiscardTemplateChanges from '../../actions/ui/DiscardTemplateChanges';
import SetSlideAction from '../../actions/ui/SetSlideAction';
import SaveSlideToTemplate from '../../actions/ui/SaveSlideToTemplate';
import CreateTemplateFromStep from '../../actions/ui/CreateTemplateFromStep';
import ModalManager from '../layout/ModalManager';
import { LayoutOrientations } from '../../globals';
import MessagePrompter from '../prompts/MessagePrompter';
import Scenario from '../../classes/scenarioElements/Scenario';
import MoveSlideAction from '../../actions/ui/MoveSlideAction';
import AssetsLibraryUtilities from '../filebrowser/AssetsLibraryUtilities';
import useCurrentStep from '../reactUtils/useCurrentStep';
import { getUuidv4 } from '../../classes/utilities/Misc';
import UIUtils from '../../classes/utilities/UIUtils';

export default function UIEditor(props)
{
	const dispatch = useDispatch();

	//global ui store
	const previewScale = useSelector(state => state.uiModule.previewScale);
	const currentStep = useCurrentStep();
	const activeTemplate = useSelector(state => props.slide && state.uiModule.slideMaster[props.id] ? state.uiModule.slideMaster[props.id].active : currentStep && state.uiModule.ui[currentStep.id] ? state.uiModule.ui[currentStep.id].active : null, shallowEqual);
	const currentStepUi = useSelector(state => props.slide && state.uiModule.slideMaster[props.id] ? null : currentStep && state.uiModule.ui[currentStep.id] ? state.uiModule.ui[currentStep.id] : null, shallowEqual);
	const currentSlide = useSelector(state => state.uiModule.slideMaster[currentStepUi?.slide]);
	const currentSlideUi = useSelector(state => state.uiModule.slideMaster[props.id]);
	const allSlides = useSelector(state => state.uiModule.slideMaster);
	const editingSlide = useSelector(state => state.uiModule.editingSlide, shallowEqual);
	const slideBackup = useSelector(state => state.uiModule.backupSlide);
	
	const resultsStep = UIUtils.findStepWithUiTemplate(UITemplateTypes.QUIZ_RESULTS);
	
	var blockingStyle = {}
	const blockingClass = (() =>
	{
		if (editingSlide)
		{
			if (currentStepUi) return "backdropBlocking";
			blockingStyle.top = "30px";
			if (props.id !== editingSlide)
			{
				return "backdropBlocking";
			}
		}
		else
		{
			if (currentSlideUi && props.id !== editingSlide)
			{
				blockingStyle.top = "30px";
				return "backdropBlocking";
			}
		}
		return null;
	})();

	const editSlideButton = (() =>
	{
		if(props.hideHeader) return null;

		if (currentSlideUi)
		{
			if (editingSlide)
			{
				if (props.id === editingSlide)
				{
					return [
						<Button
							label={"Discard"}
							category={"SlideMaster"}
							data-name={"slide-discardedit-template"}
							key={"uiTemplateDiscardChanges"}
							iconRenderer={<TrashIcon scale={0.75} />}
							onClick={(e) =>
							{
								new DiscardTemplateChanges(editingSlide, slideBackup);
								dispatch(StoreActions.ui.setBackupSlide(null));
								dispatch(StoreActions.ui.setEditingSlide(""));
							}}
						/>,
						<Spacer key={"uiSpacerKey"} size={{ x: "2px" }} />,
						<Button
							label={"Done"}
							category={"SlideMaster"}
							data-name={"slide-doneedit-template"}
							key={"uiTemplateChangeTepmlate"}
							iconRenderer={<SyncUITemplateIcon scale={0.75} />}
							onClick={(e) =>
							{
								dispatch(StoreActions.ui.setBackupSlide(null));
								dispatch(StoreActions.ui.setEditingSlide(""));
							}}
						/>,
					]
				}
			}
			return <Button
				label={"Edit"}
				disabled={currentStep.isDefault()}
				category={"SlideMaster"}
				data-name={"slide-edit-template"}
				key={"uiTemplateEditSlide"}
				iconRenderer={<PencilIcon scale={0.75} />}
				onClick={(e) =>
				{
					dispatch(StoreActions.ui.setBackupSlide({ ...currentSlideUi }));
					dispatch(StoreActions.ui.setEditingSlide(props.id));
				}}
			/>
		}

		return <></>
	})();

	function isFullySynced(deep = false)
	{
		if (!currentStepUi.synced) return false;
		if (deep)
		{
			if (!currentStepUi[currentStepUi.active]) return true;
			for (let [subCompKey, subComp] of Object.entries(currentStepUi[currentStepUi.active]))
			{
				if (!currentStepUi[currentStepUi.active][subCompKey])
				{

					continue;
				}
				for (let [propKey, prop] of Object.entries(subComp))
				{
					if (!currentStepUi[currentStepUi.active][subCompKey][propKey] ||
						currentStepUi[currentStepUi.active][subCompKey][propKey].type === 'group' ||
						currentStepUi[currentStepUi.active][subCompKey][propKey].type === 'camera')
					{

						continue;
					}
					if (prop.changed && prop.changed === true) return false;
				}
			}
		}
		return true;
	}

	function getAllSlides()
	{
		let slides = [{ display: UITemplateTypes.NONE, value: "" }];
		let entries = Object.entries(allSlides).filter(slide => slide[1].type).sort(function (a, b) { return a[1]?.name?.localeCompare(b[1]?.name) });
		for (let [key, value] of entries)
		{
			slides.push({ display: value.name, value: key, icon: <UiTemplateIcon scale={0.7} svgstyle={listTemplateTypes().includes(key) ? { fill: "#FFD700" } : { fill: "#C1C1C1" }} /> });
		}

		return slides;
	}

	function renderTemplate()
	{
		const templateProps = {
			key: props.id ?? currentStep.id,
			step: currentStep.id,
			slide: props.id ?? null,
			context: "editor",
			template: activeTemplate,
			scale: previewScale / 60,
		}

		switch (activeTemplate)
		{
			case UITemplateTypes.BLANK:
				return <Blank {...templateProps} />
			case UITemplateTypes.INTRO_SPLASH:
				return <IntroSplash {...templateProps} />
			case UITemplateTypes.TITLE:
				return <Title {...templateProps} />
			case UITemplateTypes.EULA:
				return <EULA {...templateProps} />
			case UITemplateTypes.BRANCH:
				return <Branch {...templateProps} />
			case UITemplateTypes.CONTENT:
				return <Content {...templateProps} />
			case UITemplateTypes.TITLE_MENU_6:
				return <TitleMenu6 {...templateProps} />
			case UITemplateTypes.TITLE_MENU_12:
				return <TitleMenu12 {...templateProps} />
			case UITemplateTypes.FULL_PAGE_TEXT:
				return <FullPageText {...templateProps} />
			case UITemplateTypes.FINISH_PROCEDURE:
				return <FinishProcedure {...templateProps} />
			case UITemplateTypes.QUIZ_RESULTS:
				return <QuizResults {...templateProps} />
			case UITemplateTypes.CUSTOM:
				return <Custom {...templateProps} />
			default:
				return null;
		}
	}

	//render
	if (!currentStep && !props.slide)
		return null;

	return (
		//Trackers UI Editor
		currentStep.trackers && !currentSlideUi ? <UITrackersEditor />
			: //Original UI Editor
			<Panel data-name={"ui-editor-root"}
				style={{ position: "relative", }} // temporary fix until Chrome fixes their issue with draggables, introduced with Chrome v106
			>
				{
					props.hideHeader || !currentSlideUi ? null :
						<Panel className={"centerContentHorizontally"} orientation={LayoutOrientations.Horizontal} size={{ y: "30px" }}>
							<Spacer size={{ x: "10px" }} />
							<UiTemplateIcon scale={1.2} svgstyle={listTemplateTypes().includes(props.id) ? { fill: "#FFD700", transform: "translate(0px, 2px)" } : {}} />
							<span style={{ fontSize: "125%", overflow: 'hidden', whiteSpace: 'nowrap' }}>{currentSlideUi.name}</span>
							<Spacer stretchTo={{ x: "100%" }} />
							{editSlideButton}
						</Panel>
				}
				<div className={["objectInspectorContentUI", (blockingClass ?? "")].join(' ')} style={blockingStyle}>
					{
						///*currentSlideUi && activeTemplate !== UITemplateTypes.CUSTOM*/ false ? null :
						props.hideGeneralSection ? null :
							<PropertyGroup title="General">
								{
									currentSlideUi ? null :
										<DropdownProperty label="Select Template"
											data-name={"ui-template"}
											value={currentStepUi && currentStepUi.slide ? currentStepUi.slide : ""}
											searchable={true}
											cellclass={"dropDownWidthFix"}
											noMaxWidth={true}
											onChangeCallback={(v) =>
											{
												if (currentStep)
												{
													// check if another step uses the quiz results template, and warn the user if true
													if (v === UITemplateTypes.QUIZ_RESULTS)
													{
														for (const [key, value] of Object.entries(store.getState().uiModule.ui))
														{
															if (value.active === UITemplateTypes.QUIZ_RESULTS && key !== currentStep.id)
															{
																const stepInstance = Scenario.getStep(key);
																if(!stepInstance) continue;

																MessagePrompter.showWarning({
																	title: "Move Results",
																	actions: [
																		{
																			label: "Move Results Step",
																			onClick: () =>
																			{
																				new MoveSlideAction(stepInstance, currentStep);
																			}
																		},
																		{ label: "Cancel" },
																	],
																	content:
																		<div>
																			<p>
																				You can only have a Quiz Results Template assigned to one step. Moving the results UI template to this step will remove the current one from:
																			</p>
																			<p>
																				Sequence: {stepInstance.sequence.name}
																				<br />
																				Step: {stepInstance.name}
																			</p>
																		</div>
																});

																return;
															}
														}
													}

													new SetSlideAction([currentStep.id], v);
												}
											}}
											options={getAllSlides()}
										/>
								}
								{
									!currentSlideUi || activeTemplate !== UITemplateTypes.CUSTOM ? null :
										<>
											<Button
												label={"Edit Custom Template"}
												category={"SlideMaster"}
												data-name={"slide-edit-custom"}
												iconRenderer={<PencilIcon />}
												onClick={() =>
												{
													ModalManager.addModal({
														content:
															<CustomSlideEditor
																step={currentStep.id}
																slide={props.id}
															/>
													});
												}}
											/>
											<Spacer size={{ y: "10px" }} />
										</>
								}
								{
									!props.id ? null :
										!listTemplateTypes().includes(props.id) ? null :
											<>
												<Button
													label={"Reset to default"}
													category={"SlideMaster"}
													data-name={"slide-edit-reset-to-default"}
													iconRenderer={<SyncUITemplateIcon scale={0.75} />}
													onClick={() =>
													{
														new ResetTemplateAction(props.id);
													}}
												/>
												<Spacer size={{ y: "10px" }} />
											</>
								}
								{
									!currentStepUi ? null :
										<>
											<Button
												label={"Apply changes to main template"}
												disabled={(currentSlide && currentStepUi && isFullySynced(true)) || !currentStepUi?.slide ? true : false}
												category={"SlideMaster"}
												data-name={"slide-saveto-template"}
												iconRenderer={<SyncUITemplateIcon scale={0.75} />}
												onClick={(e) =>
												{
													if (currentSlide)
													{
														new SaveSlideToTemplate(currentStep);
													}
													else
													{
														new CreateTemplateFromStep(currentStep, getUuidv4());
													}
												}}
											/>
											<Spacer size={{ y: "10px" }} />
										</>
								}
								{
									!currentStepUi || (currentStep === resultsStep) ? null :
										<Button
											label={"Save as"}
											disabled={!currentStepUi.active || currentStepUi.active === UITemplateTypes.NONE ? true : false}
											category={"SlideMaster"}
											data-name={"slide-saveasto-template"}
											iconRenderer={<SyncUITemplateIcon scale={0.75} />}
											onClick={(e) =>
											{
												let newId = getUuidv4();
												new CreateTemplateFromStep(currentStep, newId);
												
												AssetsLibraryUtilities.selectAssets([newId], { autoScroll: true, autoExpand: true, autoRename: true });
											}}
										/>
								}
							</PropertyGroup>
					}
					{
						renderTemplate()
					}
				</div>
			</Panel>
	)
}
